import { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { motion } from 'motion/react';
import { WeaveLogo } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SettingsSection } from '@frontend/settings-ui';
import {
  useShellTheme,
  compressThemeObject,
  decompressThemeObject,
  type ShellColorTheme,
  type ShellColor,
} from '@frontend/shell-theme';
import { theme } from '@frontend/theme';
import {
  NakedButton,
  ColorPicker,
  usePopoverDialog,
  PopoverDialog,
  TextButton,
  Heading,
  Text,
  TextField,
  PrimaryButton,
  SecondaryButton,
  useTooltip,
  useForm,
  BannerNotification,
} from '@frontend/design-system';
import { FontSizeCustomizer, useFontSizeCustomizerStore } from './font-size-customizer';
import { saveShellThemeToLocalStorage } from './helper';

const WEAVE_THEME_CODE_PREFIX = 'W-THEME-';
const hasShellThemeEnabled = window?.shell?.isShell && window?.shell?.featureAvailability?.has('shell-theme');

interface Props {
  newUserOnboardingVersion?: boolean;
}

export const ShellThemeSwitcher = ({ newUserOnboardingVersion }: Props) => {
  const { colorObject, setShellColor } = useShellTheme('colorObject', 'setShellColor');
  const [selectedTheme, setSelectedTheme] = useState<ShellColorTheme>(colorObject);
  const { t } = useTranslation();
  const hasChangedFontScale = useFontSizeCustomizerStore((state) => state.hasScaleChanged);

  const isSameTheme = isEqual(selectedTheme, colorObject);

  const handleCancel = () => {
    setShellColor(selectedTheme);
    useFontSizeCustomizerStore.getState().resetScale();
  };

  const handleSave = () => {
    setSelectedTheme(colorObject);
    saveShellThemeToLocalStorage(colorObject);
    useFontSizeCustomizerStore.getState().handleScaleApply();
  };

  return (
    <>
      <Heading
        level={3}
        css={css`
          margin: ${theme.spacing(2, 0)};
        `}
      >
        {t('Color Preferences')}
      </Heading>
      <SettingsSection
        removeDefaultStyles={newUserOnboardingVersion}
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        `}
      >
        {!hasShellThemeEnabled && (
          <div
            css={css`
              position: absolute;
              top: 50%;
              left: 50%;
              z-index: 1;
              transform: translate(-50%, -50%);
            `}
          >
            <BannerNotification
              status='info'
              message={t(
                'This feature is only available in the desktop app. Please switch to the desktop app to customize your theme.'
              )}
            />
          </div>
        )}
        <div
          css={[
            css`
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 500px;
              background: ${theme.colors.neutral5};
              border-radius: ${theme.borderRadius.medium};
              border: 1px solid ${theme.colors.neutral20};
              padding: ${theme.spacing(2)};
            `,
            !hasShellThemeEnabled &&
              css`
                opacity: 0.4;
              `,
            newUserOnboardingVersion &&
              css`
                min-width: 650px;
              `,
          ]}
        >
          <WeaveAppSkeleton />
        </div>
        {hasShellThemeEnabled && (
          <>
            <ShellColorButtonContainer />
            {!newUserOnboardingVersion && <ShareImportTheme />}
          </>
        )}
      </SettingsSection>
      <FontSizeCustomizer />
      {hasShellThemeEnabled && !newUserOnboardingVersion && (
        <div
          css={css`
            display: flex;
            gap: ${theme.spacing(2)};
            margin-top: ${theme.spacing(2)};
          `}
        >
          <PrimaryButton
            onClick={handleSave}
            disabled={isSameTheme && !hasChangedFontScale}
            size='tiny'
            trackingId='theme-color-save'
          >
            {t('Save')}
          </PrimaryButton>
          <SecondaryButton onClick={handleCancel} disabled={isSameTheme && !hasChangedFontScale} size='tiny'>
            {t('Cancel')}
          </SecondaryButton>
        </div>
      )}
    </>
  );
};

const ShareImportTheme = () => {
  return (
    <div
      css={css`
        display: flex;
        gap: ${theme.spacing(1)};
        margin-top: ${theme.spacing(2)};
      `}
    >
      <ShareTheme />
      <ImportTheme />
    </div>
  );
};

const ShareTheme = () => {
  const { colorObject } = useShellTheme('colorObject');
  const { t } = useTranslation();
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    if (copiedCode) {
      const timeout = setTimeout(() => {
        setCopiedCode(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
    return;
  }, [copiedCode]);

  const handleShareTheme = () => {
    const encoded = compressThemeObject(colorObject);
    const themeCode = `${WEAVE_THEME_CODE_PREFIX}${encoded}`;
    navigator.clipboard.writeText(themeCode);
    setCopiedCode(true);
  };

  return (
    <>
      <TextButton
        css={css`
          color: ${theme.font.colors.primary};
          font-weight: ${theme.font.weight.bold};
        `}
        icon={() => <Icon name='share-small' />}
        {...triggerProps}
        onClick={handleShareTheme}
      >
        {t('Share Theme')}
      </TextButton>
      <Tooltip {...tooltipProps}>
        {!copiedCode ? (
          <span
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(0.5)};
            `}
          >
            <Icon name='copy-small' />
            {t('Copy Theme Code')}
          </span>
        ) : (
          <span
            css={css`
              display: flex;
              align-items: center;
              gap: ${theme.spacing(0.5)};
            `}
          >
            <Icon name='check-small' color='success' />
            {t('Copied Theme Code')}
          </span>
        )}
      </Tooltip>
    </>
  );
};

const ImportTheme = () => {
  const { t } = useTranslation('base');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    placement: 'top-start',
  });

  return (
    <>
      <TextButton
        {...getTriggerProps()}
        css={css`
          color: ${theme.font.colors.primary};
          font-weight: ${theme.font.weight.bold};
        `}
        icon={() => <Icon name='download-small' />}
      >
        {t('Import Theme')}
      </TextButton>
      <PopoverDialog
        css={css`
          max-width: 400px;
          padding: ${theme.spacing(4)};
          border-radius: ${theme.borderRadius.medium};
        `}
        {...getDialogProps()}
        returnFocus={false}
      >
        <ImportThemeDialog />
      </PopoverDialog>
    </>
  );
};

const ImportThemeDialog = () => {
  const { setShellColor } = useShellTheme('setShellColor');
  const decodingError = useRef('');
  const { t } = useTranslation('base');

  const { formProps, getFieldProps, validate } = useForm({
    fields: {
      themeInput: {
        type: 'text',
        required: true,
        transformValue: (value) => {
          decodingError.current = '';
          return value?.replace(WEAVE_THEME_CODE_PREFIX, '');
        },
        validator: () => {
          return decodingError.current;
        },
      },
    },

    onSubmit: (values) => {
      try {
        if (!values.themeInput) return;

        const decoded = decompressThemeObject(values.themeInput);
        setShellColor(decoded);
      } catch (error) {
        if (error instanceof Error) {
          decodingError.current = error.message;
          validate();
        }
      }
    },
  });

  return (
    <form {...formProps}>
      <Heading level={2}>{t('Import Your Theme')}</Heading>
      <Text>{t('Paste your codes into this box to generate your gradient.')}</Text>
      <TextField
        label=''
        css={css`
          margin-top: ${theme.spacing(2)};
        `}
        startAdornment={
          <span
            css={css`
              white-space: nowrap;
              color: ${theme.font.colors.subdued};
              margin-right: ${theme.spacing(-1)};
            `}
          >
            {WEAVE_THEME_CODE_PREFIX}
          </span>
        }
        {...getFieldProps('themeInput')}
      />
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin-top: ${theme.spacing(4)};
        `}
      >
        <PrimaryButton type='submit' size='tiny'>
          {t('Import Theme')}
        </PrimaryButton>
      </div>
    </form>
  );
};

const ShellColorButtonContainer = () => {
  const { setShellColor, allColors } = useShellTheme('setShellColor', 'allColors');
  return (
    <div
      css={css`
        display: flex;
        gap: ${theme.spacing(1.5)};
        margin-top: ${theme.spacing(2)};
        flex-wrap: wrap;
      `}
    >
      {Object.keys(allColors).map((color, index) => (
        <ShellColorButton
          key={index}
          color={allColors[color as ShellColor]['shellColor']}
          onClick={() => {
            setShellColor(color as ShellColor);
          }}
        />
      ))}
      <RandomizeThemeButton />
      <BrandingPicker />
    </div>
  );
};

const BrandingPicker = () => {
  const { randomizeTheme } = useShellTheme('randomizeTheme');
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement>({
    placement: 'bottom-start',
  });

  return (
    <>
      <NakedButton css={roundButtonStyle} {...getTriggerProps()} trackingId='theme-color-branding-picker'>
        <Icon name='eyedropper-small' />
      </NakedButton>
      <PopoverDialog
        css={css`
          border-radius: ${theme.borderRadius.medium};
        `}
        {...getDialogProps()}
        returnFocus={false}
      >
        <ColorPicker shouldApply onChange={(color) => randomizeTheme(color, 2)} />
      </PopoverDialog>
    </>
  );
};

const RandomizeThemeButton = () => {
  const { randomizeTheme } = useShellTheme('randomizeTheme');

  return (
    <NakedButton css={roundButtonStyle} onClick={() => randomizeTheme()} trackingId='theme-color-randomize'>
      <Icon name='shuffle-small' />
    </NakedButton>
  );
};

type ShellColorButtonProps = {
  color: string;
  onClick: () => void;
};

const ShellColorButton = ({ color, onClick }: ShellColorButtonProps) => {
  return (
    <NakedButton
      onClick={onClick}
      css={css`
        background: ${color};
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: none;
      `}
      trackingId='theme-color-change'
    />
  );
};

const WeaveAppSkeleton = () => {
  const { colorObject } = useShellTheme('colorObject');

  return (
    <motion.div
      css={css`
        width: 500px;
        height: 400px;
        border-radius: ${theme.borderRadius.medium};
        box-shadow: ${theme.shadows.floating};
        position: relative;
        background: ${theme.colors.white};
        overflow: hidden;
      `}
    >
      <motion.span
        initial={{ background: colorObject.shellColor }}
        animate={{ background: colorObject.shellColor }}
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 24px;
          z-index: 1;
        `}
      />
      <motion.span
        initial={{ background: colorObject.shellColor }}
        animate={{ background: colorObject.shellColor }}
        css={css`
          position: absolute;
          bottom: 0;
          left: 0;
          height: 82%;
          width: 24px;
          border-radius: 0 10px 0 0;
        `}
      />
      <div
        css={css`
          position: absolute;
          top: 32px;
          left: 8px;
          width: 100px;
          height: 100px;
          z-index: 2;
        `}
      >
        <WeaveLogo />
      </div>
    </motion.div>
  );
};

const roundButtonStyle = css`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
